import type { Board } from '../model/Board';
import {
  fetchBoardsFromSupabase,
  fetchBoardByIdFromSupabase,
  fetchBoardTitleFromSupabase,
  addUserToBoardWaitingListInSupabase,
  removeUserFromBoardWaitingListInSupabase,
} from '@/board/external/board.api';
import { boardTitleKey, storage } from '@/shared/lib/storage';

export async function fetchBoardTitle(boardId: string): Promise<string> {
  try {
    const cacheKey = boardTitleKey(boardId);
    const cachedTitle = storage.get(cacheKey);
    if (cachedTitle) {
      return cachedTitle;
    }

    const title = await fetchBoardTitleFromSupabase(boardId);
    if (title !== 'Board not found') {
      storage.set(cacheKey, title);
    }
    return title;
  } catch (error) {
    console.error('Error fetching board title:', error);
    return 'Error loading title';
  }
}

export async function fetchBoardsWithUserPermissions(userId: string): Promise<Board[]> {
  // 실패를 빈 목록으로 삼키지 않고 던져, 소비자(React Query 에러 UI/로더)와 Sentry가 실패를 보게 한다.
  return fetchBoardsFromSupabase(userId);
}

/**
 * ID로 보드 정보를 가져옵니다
 * @param boardId 가져올 보드 ID
 * @returns Promise<Board | null> 보드 정보 또는 존재하지 않는 경우 null
 */
export async function fetchBoardById(boardId: string): Promise<Board | null> {
  if (!boardId) {
    console.warn('fetchBoardById called with empty boardId');
    return null;
  }

  try {
    const board = await fetchBoardByIdFromSupabase(boardId);
    if (!board) {
      console.warn(`fetchBoardById: board not found in Supabase for id "${boardId}". Check if the board was migrated.`);
    }
    return board;
  } catch (error) {
    console.error(`Error fetching board with id ${boardId}:`, error);
    return null;
  }
}

/**
 * 보드의 대기자 목록에 사용자를 추가합니다
 * @param boardId 보드 ID
 * @param userId 사용자 ID
 * @returns Promise<boolean> 성공 여부
 */
export async function addUserToBoardWaitingList(boardId: string, userId: string): Promise<boolean> {
  if (!boardId || !userId) {
    console.warn('addUserToBoardWaitingList called with empty boardId or userId');
    return false;
  }

  try {
    await addUserToBoardWaitingListInSupabase(boardId, userId);
    return true;
  } catch (error) {
    console.error(`Error adding user ${userId} to board ${boardId} waiting list:`, error);
    return false;
  }
}

/**
 * 보드의 대기자 목록에서 사용자를 제거합니다
 * @param boardId 보드 ID
 * @param userId 사용자 ID
 * @returns Promise<boolean> 성공 여부
 */
export async function removeUserFromBoardWaitingList(boardId: string, userId: string): Promise<boolean> {
  if (!boardId || !userId) {
    console.warn('removeUserFromBoardWaitingList called with empty boardId or userId');
    return false;
  }

  try {
    await removeUserFromBoardWaitingListInSupabase(boardId, userId);
    return true;
  } catch (error) {
    console.error(`Error removing user ${userId} from board ${boardId} waiting list:`, error);
    return false;
  }
}

/**
 * 보드의 시작 날짜를 포맷팅합니다
 * @param board 보드 객체
 * @returns string 포맷팅된 날짜 문자열
 */
export function formatStartDate(board: Board | null | undefined): string {
  if (!board?.firstDay) {
    return '미정';
  }
  return board.firstDay.toDate().toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' });
}
